import { defineStore } from 'pinia'
import { subscriptionService } from '@/services/subscriptionService'
import type { MyOfferResponse, Plan, Product, ProductPurchase } from '@/types/subscription'
import { handleAuthError, extractErrorMessage } from '@/utils/storeHelpers'
import { normalizePlansResponse } from '@/utils/subscriptionHelpers'
import { APP_ROUTES } from '@/const/appRoutes'

const responseStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status

/** Paid purchase whose access window hasn't expired yet (active or scheduled to start). */
const isUnexpiredPaidPurchase = (purchase: ProductPurchase | null): boolean =>
  Boolean(
    purchase &&
      purchase.status === 'paid' &&
      purchase.expires_at &&
      new Date(purchase.expires_at) > new Date()
  )

export const usePlansStore = defineStore('plans', {
  state: () => ({
    plans: [] as Plan[],
    assignedProduct: null as Product | null,
    activePurchase: null as ProductPurchase | null,
    latestPurchase: null as ProductPurchase | null,
    loading: false,
    error: null as string | null,
  }),

  getters: {
    hasPlans: (state) => state.plans.length > 0,
    hasProductOffer: (state) => Boolean(state.assignedProduct),
    /** Paid purchase that is active now or scheduled to start (not yet expired). */
    unexpiredPurchase: (state) =>
      isUnexpiredPaidPurchase(state.latestPurchase) ? state.latestPurchase : null,
    /** True when the offered product was already bought and its access hasn't expired. */
    productAlreadyPurchased: (state) =>
      Boolean(state.assignedProduct) && isUnexpiredPaidPurchase(state.latestPurchase),
    /** True when user can buy the offered product (no active or scheduled purchase). */
    canPurchaseProduct: (state) =>
      Boolean(state.assignedProduct) && !isUnexpiredPaidPurchase(state.latestPurchase),

    getPlanById: (state) => (id: number) => {
      return state.plans.find(plan => plan.id === id)
    },
  },

  actions: {
    async fetchPlans() {
      this.loading = true
      this.error = null

      try {
        // Prefer my-offer (plan + product); fall back to assigned-plan for older clients
        try {
          const offerResponse = await subscriptionService.getMyOffer()
          const offer = offerResponse.data as MyOfferResponse
          this.plans = offer.assigned_plan ? [offer.assigned_plan] : []
          this.assignedProduct = offer.assigned_product
          this.activePurchase = offer.active_purchase
          this.latestPurchase = offer.latest_purchase
        } catch (offerError) {
          if (responseStatus(offerError) === 404) {
            const response = await subscriptionService.getAssignedPlans()
            this.plans = normalizePlansResponse(response.data)
            this.assignedProduct = null
            this.activePurchase = null
            this.latestPurchase = null
          } else {
            throw offerError
          }
        }
      } catch (error) {
        if (handleAuthError(error)) {
          this.error = 'Current user is not authorized to access subscription plans'
          return
        }

        if (responseStatus(error) === 404) {
          this.error = 'Request failed with status code 404'
        } else {
          this.error = extractErrorMessage(error, 'Failed to fetch plans')
        }
      } finally {
        this.loading = false
      }
    },

    async createProductPurchaseCheckout(productId: string) {
      const response = await subscriptionService.createProductPurchaseCheckoutSession({
        product_id: productId,
        success_url: `${window.location.origin}${APP_ROUTES.TUITION}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}${APP_ROUTES.TUITION}?cancelled=true`,
      })
      return response.data
    },
  }
})
